import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { TrialBalanceDetailDialog } from '../shared/TrialBalanceDetailDialog';
import { LedgerReportDialog } from '../shared/LedgerReportDialog';
import { MondayFinalModal } from '../shared/MondayFinalModal';
import { HistoricalRecordsModal } from '../shared/HistoricalRecordsModal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { 
  RefreshCw, Save, Plus, Search, Trash2, FileSpreadsheet, FileText, 
  CheckCircle, X, Edit, DollarSign, Users, Printer, Lock, History, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { mondaySettlingApi, settlementsApi, clientsApi } from '../../lib/api';
import { useSocketStore } from '../../store/socketStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status constants
const STATUS = {
  NEW: 'NEW',
  SETTLED: 'SETTLED'
};

// Generate UUID
const generateId = () => `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Column definitions for keyboard navigation
const COLUMNS = [
  'tallied', 'clientName', 'displayName', 'group', 
  'clientPL', 'clientAt', 'clientCommPct',
  'parentId', 'parentAt', 'parentCommPct', 'multiplier'
];

export function SettlementPage() {
  const { subscribe } = useSocketStore();
  const navigate = useNavigate();
  
  // State
  const [clients, setClients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [allSettlements, setAllSettlements] = useState([]); // All match settlements for balance calculation
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false); // Add/Edit mode toggle
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Cell references for keyboard navigation
  const cellRefs = useRef({});
  const [focusedCell, setFocusedCell] = useState({ row: 0, col: 0 });
  
  // Settlement screen state
  const [selectedClient, setSelectedClient] = useState(null);
  const [toPayItems, setToPayItems] = useState([]);
  const [toReceiveItems, setToReceiveItems] = useState([]);
  const [clientSettlements, setClientSettlements] = useState([]); // Match settlements for selected client
  
  // Match transactions dialog state - now using TrialBalanceDetailDialog
  const [matchTxnDialog, setMatchTxnDialog] = useState({ 
    open: false, 
    clientId: null, 
    clientName: '', 
    matchIds: [], 
    sessionIds: [],
    trialBalanceItem: null
  });
  const [matchTransactions, setMatchTransactions] = useState([]);
  
  // Dialog states
  const [settleAllConfirm, setSettleAllConfirm] = useState(false);
  
  // Account Posting state
  const [postingClient, setPostingClient] = useState('');
  const [postingAmount, setPostingAmount] = useState('');
  const [postingNarration, setPostingNarration] = useState('');
  
  // Settlement CRUD state
  const [settlementDialog, setSettlementDialog] = useState({ open: false, mode: 'add', type: 'pay', item: null });
  const [settlementForm, setSettlementForm] = useState({ displayName: '', amount: '', date: new Date().toISOString().split('T')[0] });
  
  // New Settlement Enhancement dialogs state
  const [ledgerDialog, setLedgerDialog] = useState({ open: false, clientId: null, clientName: '', isHistorical: false, mondayFinalIds: [] });
  const [mondayFinalDialog, setMondayFinalDialog] = useState({ open: false, clientId: null, clientName: '', transactionCount: 0, currentBalance: 0 });
  const [historicalDialog, setHistoricalDialog] = useState({ open: false, clientId: null, clientName: '' });

  // Register cell ref
  const setCellRef = (rowIdx, colIdx, ref) => {
    const key = `${rowIdx}-${colIdx}`;
    if (ref) {
      cellRefs.current[key] = ref;
    }
  };

  // Focus cell
  const focusCell = (rowIdx, colIdx) => {
    const key = `${rowIdx}-${colIdx}`;
    const ref = cellRefs.current[key];
    if (ref) {
      ref.focus();
      if (ref.select) ref.select();
      setFocusedCell({ row: rowIdx, col: colIdx });
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIdx, colIdx, entry) => {
    const isLastColumn = colIdx === COLUMNS.length - 1;
    const isLastRow = rowIdx === entries.length - 1;
    
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      
      if (isLastColumn) {
        // Last column - move to next row or add new row
        if (isLastRow) {
          // Add new row and focus first editable column
          handleAddRow();
          setTimeout(() => focusCell(rowIdx + 1, 1), 50); // Focus clientName
        } else {
          // Move to next row first column
          focusCell(rowIdx + 1, 1);
        }
      } else {
        // Move to next column
        focusCell(rowIdx, colIdx + 1);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rowIdx < entries.length - 1) {
        focusCell(rowIdx + 1, colIdx);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rowIdx > 0) {
        focusCell(rowIdx - 1, colIdx);
      }
    } else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value?.length) {
      if (colIdx < COLUMNS.length - 1) {
        focusCell(rowIdx, colIdx + 1);
      }
    } else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      if (colIdx > 0) {
        focusCell(rowIdx, colIdx - 1);
      }
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch clients from main app
      const clientsRes = await fetch(`${API_URL}/api/clients`, {
        credentials: 'include'
      });
      
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      }
      
      // Fetch monday settling entries
      const entriesRes = await fetch(`${API_URL}/api/monday-settling/entries`, {
        credentials: 'include'
      });
      
      if (entriesRes.ok) {
        const entriesData = await entriesRes.json();
        setEntries(entriesData);
      } else if (entriesRes.status === 404) {
        setEntries([]);
      }
      
      // Fetch monday settlements (add cache-buster to prevent caching)
      const settlementsRes = await fetch(`${API_URL}/api/monday-settling/settlements?_t=${Date.now()}`, {
        credentials: 'include',
        cache: 'no-store'
      });
      
      if (settlementsRes.ok) {
        const settlementsData = await settlementsRes.json();
        setSettlements(settlementsData);
      } else if (settlementsRes.status === 404) {
        setSettlements([]);
      }
      
      // Fetch match settlements (from declared matches)
      try {
        const matchSettlements = await settlementsApi.list();
        setAllSettlements(matchSettlements || []);
      } catch (e) {
        console.error('Error fetching match settlements:', e);
        setAllSettlements([]);
      }
      
    } catch (error) {
      console.error('Failed to load data', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const unsub = subscribe('SETTLEMENT_POSTED', () => {
      console.log('SETTLEMENT_POSTED event received');
      fetchData();
    });
    return () => unsub();
  }, [fetchData, subscribe]);

  // Calculation logic from document
  const calculateEntry = (entry) => {
    const pl = parseFloat(entry.clientPL) || 0;
    const clientAt = parseFloat(entry.clientAt) || 1;
    const parentAt = parseFloat(entry.parentAt) || 1;
    const clientCommPct = parseFloat(entry.clientCommPct) || 0;
    const parentCommPct = parseFloat(entry.parentCommPct) || 0;
    
    // grossClient = P/L × client @
    const grossClient = pl * clientAt;
    // grossParent = P/L × parent @
    const grossParent = pl * parentAt;
    
    // Commission only on loss (negative gross)
    const clientCommAmount = grossClient < 0 ? grossClient * (clientCommPct / 100) : 0;
    const parentCommAmount = grossParent < 0 ? grossParent * (parentCommPct / 100) : 0;
    
    // clientAmount = grossClient - clientCommAmount
    const clientAmount = grossClient - clientCommAmount;
    // parentAmount = grossParent - parentCommAmount
    const parentAmount = grossParent - parentCommAmount;
    
    // nettAmount = -1 × (clientAmount - parentAmount)
    const nettAmount = -1 * (clientAmount - parentAmount);
    
    return {
      ...entry,
      grossClient: Math.round(grossClient * 100) / 100,
      grossParent: Math.round(grossParent * 100) / 100,
      clientCommAmount: Math.round(clientCommAmount * 100) / 100,
      parentCommAmount: Math.round(parentCommAmount * 100) / 100,
      clientAmount: Math.round(clientAmount * 100) / 100,
      parentAmount: Math.round(parentAmount * 100) / 100,
      nettAmount: Math.round(nettAmount * 100) / 100
    };
  };

  // Add new row at bottom
  const handleAddRow = () => {
    const newEntry = {
      id: generateId(),
      clientName: '',
      displayName: '',
      userType: 'User',
      group: '',
      clientPL: 0,
      clientAt: 1.00,
      clientCommPct: 0,
      clientAmount: 0,
      parentId: '',
      parentAt: 1.00,
      parentCommPct: 0,
      parentAmount: 0,
      multiplier: 1,
      nettAmount: 0,
      status: STATUS.NEW,
      tallied: false,
      createdAt: new Date().toISOString()
    };
    
    setEntries(prev => [...prev, newEntry]);
    setHasChanges(true);
  };

  // Update entry
  const handleUpdateEntry = (id, field, value) => {
    setEntries(prevEntries => {
      return prevEntries.map(entry => {
        if (entry.id === id) {
          const updated = { ...entry, [field]: value };
          
          // If client P/L is modified after settlement, reset to NEW
          if (field === 'clientPL' && entry.status === STATUS.SETTLED) {
            updated.status = STATUS.NEW;
          }
          
          // Recalculate if financial field changed
          if (['clientPL', 'clientAt', 'clientCommPct', 'parentAt', 'parentCommPct', 'multiplier'].includes(field)) {
            return calculateEntry(updated);
          }
          
          return updated;
        }
        return entry;
      });
    });
    setHasChanges(true);
  };

  // Delete row
  const handleDeleteRow = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    setHasChanges(true);
  };

  // Check for duplicate display name
  const checkDuplicateDisplayName = (displayName, excludeId) => {
    return entries.some(
      e => e.displayName?.toLowerCase() === displayName?.toLowerCase() && e.id !== excludeId
    );
  };

  // Settle single entry - posts to both client and parent accounts with P&L double-entry
  const handleSettleEntry = async (entry) => {
    try {
      // Find P&L account
      const plAccount = clients.find(c => c.is_system_account && c.system_account_type === 'PL');
      if (!plAccount) {
        toast.error('P&L account not found. Please contact support.');
        return;
      }

      // Update entry status
      const updatedEntry = {
        ...entry,
        status: STATUS.SETTLED,
        clientPL: 0,
        clientAmount: 0,
        parentAmount: 0,
        nettAmount: 0,
        settledAt: new Date().toISOString()
      };
      
      // Create settlement records with DOUBLE-ENTRY system
      // Every entry to a client/parent account has a corresponding opposite entry to P&L
      const settlementRecords = [];
      const timestamp = new Date().toISOString();
      
      // Client settlement record with P&L counterpart
      // Client amount is inverted: negative becomes positive (To Receive), positive becomes negative (To Pay)
      if (entry.clientAmount !== 0) {
        const clientSettlementAmount = -1 * entry.clientAmount;
        
        // Entry for Client account
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'CLIENT',
          clientId: entry.clientName,
          clientName: entry.clientName,
          displayName: entry.displayName,
          amount: clientSettlementAmount,
          settledAt: timestamp
        });
        
        // Opposite entry for P&L account (double-entry)
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${entry.displayName} (${entry.clientName})`,
          amount: -1 * clientSettlementAmount, // Opposite of client entry
          settledAt: timestamp
        });
      }
      
      // Parent settlement record with P&L counterpart
      // Parent amount: negative → To Pay (keep negative), positive → To Receive (keep positive)
      if (entry.parentId && entry.parentAmount !== 0) {
        // Entry for Parent account
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'PARENT',
          clientId: entry.parentId,
          clientName: entry.parentId,
          displayName: entry.displayName,
          amount: entry.parentAmount,
          settledAt: timestamp
        });
        
        // Opposite entry for P&L account (double-entry)
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${entry.displayName} (${entry.parentId})`,
          amount: -1 * entry.parentAmount, // Opposite of parent entry
          settledAt: timestamp
        });
      }
      
      // Save to backend
      const response = await fetch(`${API_URL}/api/monday-settling/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entry: updatedEntry,
          settlements: settlementRecords
        })
      });
      
      if (response.ok) {
        // Update local state
        setEntries(prevEntries => 
          prevEntries.map(e => e.id === entry.id ? updatedEntry : e)
        );
        setSettlements(prev => [...prev, ...settlementRecords]);
        
        toast.success(`Settled: ${entry.displayName || entry.clientName}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Settlement failed');
      }
    } catch (error) {
      console.error('Settlement failed', error);
      toast.error('Settlement failed');
    }
  };

  // Settle all entries
  const handleSettleAll = async () => {
    const unsettledEntries = entries.filter(e => e.status === STATUS.NEW && (e.clientAmount !== 0 || e.parentAmount !== 0));
    
    if (unsettledEntries.length === 0) {
      toast.info('No entries to settle');
      return;
    }
    
    try {
      for (const entry of unsettledEntries) {
        await handleSettleEntry(entry);
      }
      toast.success(`Settled ${unsettledEntries.length} entries`);
      setSettleAllConfirm(false);
    } catch (error) {
      toast.error('Failed to settle all entries');
    }
  };

  // Save all entries
  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/monday-settling/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ entries })
      });
      
      if (response.ok) {
        toast.success('Data saved successfully');
        setHasChanges(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save data');
    }
  };

  // Clear all data
  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/monday-settling/clear`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      setEntries([]);
      setSettlements([]);
      toast.success('Data cleared');
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    const data = entries.map((e, idx) => ({
      'S.No': idx + 1,
      'Client Name': e.clientName,
      'Display Name': e.displayName,
      'User Type': e.userType,
      'Group': e.group,
      'Client P/L': e.clientPL,
      'Client @': e.clientAt,
      'Client Comm %': e.clientCommPct,
      'Client Amount': e.clientAmount,
      'Parent ID': e.parentId,
      'Parent @': e.parentAt,
      'Parent Comm %': e.parentCommPct,
      'Parent Amount': e.parentAmount,
      'Nett Amount': -1 * e.nettAmount,
      'Status': e.status
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OnlineClient');
    XLSX.writeFile(wb, `monday_settling_${format(new Date(), 'yyyyMMdd')}.xlsx`);
    toast.success('Excel exported');
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.setTextColor(212, 175, 55);
    doc.text('Monday Settling - Online Client', 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    const headers = [['S.No', 'Client', 'Display', 'P/L', 'Client Amt', 'Parent', 'Parent Amt', 'Nett', 'Status']];
    const rows = entries.map((e, idx) => [
      idx + 1,
      e.clientName,
      e.displayName,
      e.clientPL?.toFixed(2),
      e.clientAmount?.toFixed(2),
      e.parentId,
      e.parentAmount?.toFixed(2),
      (-1 * (e.nettAmount || 0)).toFixed(2),
      e.status
    ]);
    
    doc.autoTable({
      head: headers,
      body: rows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] }
    });
    
    doc.save(`monday_settling_${format(new Date(), 'yyyyMMdd')}.pdf`);
    toast.success('PDF exported');
  };

  // Calculate totals
  const totals = entries.reduce((acc, entry) => {
    acc.clientPL += parseFloat(entry.clientPL) || 0;
    acc.clientAmount += parseFloat(entry.clientAmount) || 0;
    acc.parentAmount += parseFloat(entry.parentAmount) || 0;
    acc.nettAmount += parseFloat(entry.nettAmount) || 0;
    return acc;
  }, { clientPL: 0, clientAmount: 0, parentAmount: 0, nettAmount: 0 });

  // Filter entries based on search
  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.clientName?.toLowerCase().includes(search) ||
      entry.displayName?.toLowerCase().includes(search) ||
      entry.group?.toLowerCase().includes(search)
    );
  });

  // Get row style based on status
  const getRowStyle = (entry) => {
    if (selectedEntry?.id === entry.id) return 'bg-amber-500/30 text-foreground font-semibold';
    if (entry.tallied) return 'bg-yellow-500/20';
    if (entry.status === STATUS.SETTLED) return 'bg-muted/50 text-muted-foreground';
    if (entry.status === STATUS.NEW) return 'bg-emerald-500/10';
    return '';
  };

  // Settlement screen: Select client and load To Pay / To Receive
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    
    // Filter monday-settling settlements for this client (both as client and parent)
    // Exclude locked entries (hidden after Monday Final)
    const clientSettlementsFromMonday = settlements.filter(s => 
      (s.clientName === client.name || s.clientId === client.client_id || s.clientId === client.name) &&
      !s.is_locked && s.is_visible !== false
    );
    
    // Fetch ALL settlements (MATCH + SESSION) from backend
    try {
      const allClientSettlements = await settlementsApi.list(client.client_id);
      
      // Check if this is the P&L system account
      const isPLAccount = client.is_system_account && client.system_account_type === 'PL';
      
      // Filter settlements based on account type - exclude locked entries
      let filteredSettlements = allClientSettlements.filter(s => 
        (s.source_type === 'MATCH' || s.source_type === 'SESSION' || s.match_id || s.session_id) &&
        !s.is_locked
      );
      
      // For P&L account, only show PL_BOOK entries (exclude CLIENT entries)
      if (isPLAccount) {
        filteredSettlements = filteredSettlements.filter(s => s.entry_type === 'PL_BOOK');
      }
      
      setClientSettlements(filteredSettlements);
    } catch (error) {
      console.error('Error fetching settlements:', error);
      setClientSettlements([]);
    }
    
    // To Pay: negative amounts (we owe them) - already filtered for locked
    setToPayItems(clientSettlementsFromMonday.filter(s => s.amount < 0));
    // To Receive: positive amounts (they owe us) - already filtered for locked
    setToReceiveItems(clientSettlementsFromMonday.filter(s => s.amount > 0));
  };

  // Client balance calculation - combines all settlements (monday + match settlements)
  const getClientBalance = (client) => {
    // Start with old balance (from previous Monday Finals)
    const oldBalance = client.old_balance || 0;
    
    // Monday settling settlements (To Pay / To Receive)
    const mondaySettlements = settlements.filter(s => 
      (s.clientName === client.name || s.clientId === client.client_id || s.clientId === client.name) &&
      !s.is_locked // Exclude locked entries
    );
    const mondayBalance = mondaySettlements.reduce((sum, s) => sum + (s.amount || 0), 0);
    
    // Match settlements (from declared matches) - exclude locked entries
    const matchSettlements = allSettlements.filter(s => 
      s.client_id === client.client_id && !s.is_locked
    );
    const matchBalance = matchSettlements.reduce((sum, s) => sum + (s.net_amount || 0), 0);
    
    // Total = Old Balance + Monday Settlements + Match Settlements
    return oldBalance + mondayBalance + matchBalance;
  };

  // Handle click on match/session name to show detailed transactions for selected client
  const handleMatchClick = async (settlement) => {
    if (!settlement.match_id && !settlement.session_id) return;
    
    const clientId = settlement.client_id || selectedClient?.client_id;
    const clientName = settlement.client_name || selectedClient?.name;
    
    // Prepare matchIds and sessionIds arrays
    const matchIds = settlement.match_id ? [settlement.match_id] : [];
    const sessionIds = settlement.session_id ? [settlement.session_id] : [];
    
    // Build a trialBalanceItem-like object from settlement data
    const trialBalanceItem = {
      client_id: clientId,
      client_name: clientName,
      balance: settlement.gross_amount || 0,
      net_balance: settlement.net_amount || 0,
      user_commission: settlement.commission || 0,
      is_patti: settlement.is_patti || false,
      patti_commission_received: settlement.is_patti ? Math.abs(settlement.commission || 0) : 0,
      patti_ref_commission_received: settlement.is_patti ? Math.abs(settlement.ref_commission || 0) : 0,
      ref_commission_received: settlement.entry_type === 'REF_COMMISSION' ? settlement.net_amount : 0,
      is_reference_entry: settlement.entry_type === 'REF_COMMISSION',
      commission_entries: [],
      entry_details: [{
        entry_type: settlement.entry_type || 'CLIENT',
        source: settlement.match_name || settlement.session_name || settlement.comment,
        gross: settlement.gross_amount || 0,
        commission: settlement.commission || 0,
        ref_commission: settlement.ref_commission || 0,
        net: settlement.net_amount || 0
      }]
    };
    
    setMatchTxnDialog({ 
      open: true, 
      clientId,
      clientName,
      matchIds,
      sessionIds,
      trialBalanceItem
    });
  };

  // Handle click on reference commission entry to show source breakdown
  const [refCommDialog, setRefCommDialog] = useState({ open: false, settlement: null });
  
  const handleRefCommClick = (settlement) => {
    setRefCommDialog({ open: true, settlement });
  };

  // Handle click on P&L Book entry to navigate to view-only transactions page
  const handlePLBookClick = (settlement) => {
    if (settlement.session_id) {
      navigate(`/transactions?session_id=${settlement.session_id}&match_id=${settlement.match_id}&view_only=true&from=settlement`);
    } else if (settlement.match_id) {
      navigate(`/transactions?match_id=${settlement.match_id}&view_only=true&from=settlement`);
    }
  };

  // Account Posting handler with P&L double-entry
  const handleAccountPosting = async () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const amount = parseFloat(postingAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!postingClient) {
      toast.error('Please select a target client');
      return;
    }
    
    // Find P&L account for double-entry
    const plAccount = clients.find(c => c.is_system_account && c.system_account_type === 'PL');
    if (!plAccount) {
      toast.error('P&L account not found. Please contact support.');
      return;
    }
    
    try {
      const newSettlements = [];
      const timestamp = new Date().toISOString();
      const entryId = `posting_${generateId()}`;
      
      // Account Posting Logic with DOUBLE-ENTRY:
      // If amount > 0:
      //   - Selected client: To Receive (positive amount)
      //   - Target client: To Pay (negative amount)
      //   - P&L gets opposite entries for both
      // If amount < 0:
      //   - Selected client: To Pay (negative amount)
      //   - Target client: To Receive (positive amount)
      //   - P&L gets opposite entries for both
      
      if (amount > 0) {
        // Selected client gets To Receive entry (positive amount)
        // Client Name in grid = postingClient (the source), Reference = narration
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: selectedClient.client_id,
          clientName: selectedClient.name,
          targetClientId: postingClient,
          targetClientName: postingClient, // The client selected in Account Posting dropdown
          displayName: postingClient, // Show target client as the "Client Name" in grid
          narration: postingNarration || '', // The narration text for "Reference" column
          amount: amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for selected client (negative = To Pay)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          targetClientId: selectedClient.client_id,
          targetClientName: selectedClient.name,
          displayName: selectedClient.name,
          narration: postingNarration || '',
          amount: -amount,
          settledAt: timestamp
        });
        
        // Target client gets To Pay entry (negative amount)
        // Client Name in grid = selectedClient (the destination), Reference = narration
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: postingClient,
          clientName: postingClient,
          targetClientId: selectedClient.client_id,
          targetClientName: selectedClient.name, // The selected client receiving the amount
          displayName: selectedClient.name, // Show selected client as the "Client Name" in grid
          narration: postingNarration || '',
          amount: -amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for target client (positive = To Receive)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          targetClientId: postingClient,
          targetClientName: postingClient,
          displayName: postingClient,
          narration: postingNarration || '',
          amount: amount,
          settledAt: timestamp
        });
      } else {
        // amount < 0
        // Selected client gets To Pay entry (negative amount)
        // Client Name = target client (postingClient), Reference = narration
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: selectedClient.client_id,
          clientName: selectedClient.name,
          targetClientId: postingClient,
          targetClientName: postingClient,
          displayName: postingClient, // Show target client as "Client Name"
          narration: postingNarration || '',
          amount: amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for selected client (positive = To Receive)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          targetClientId: selectedClient.client_id,
          targetClientName: selectedClient.name,
          displayName: selectedClient.name,
          narration: postingNarration || '',
          amount: -amount,
          settledAt: timestamp
        });
        
        // Target client gets To Receive entry (positive amount)
        // Client Name = selected client, Reference = narration
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: postingClient,
          clientName: postingClient,
          targetClientId: selectedClient.client_id,
          targetClientName: selectedClient.name,
          displayName: selectedClient.name,
          narration: postingNarration || '',
          amount: -amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for target client (negative = To Pay)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          targetClientId: postingClient,
          targetClientName: postingClient,
          displayName: postingClient,
          narration: postingNarration || '',
          amount: amount,
          settledAt: timestamp
        });
      }
      
      // Save to backend FIRST
      try {
        await mondaySettlingApi.createSettlements(newSettlements);
      } catch (apiError) {
        // If backend fails, still update local state but show warning
        console.warn('Backend save failed, updating local state only:', apiError);
      }
      
      // Add to settlements state
      const updatedSettlements = [...settlements, ...newSettlements];
      setSettlements(updatedSettlements);
      
      // Refresh the To Pay / To Receive lists for selected client
      const clientSettlements = updatedSettlements.filter(s => 
        s.clientName === selectedClient.name || s.clientId === selectedClient.client_id || s.clientId === selectedClient.name
      );
      setToPayItems(clientSettlements.filter(s => s.amount < 0));
      setToReceiveItems(clientSettlements.filter(s => s.amount > 0));
      
      // Clear form
      setPostingClient('');
      setPostingAmount('');
      setPostingNarration('');
      
      toast.success('Account posting saved successfully');
    } catch (error) {
      console.error('Account posting error:', error);
      toast.error('Failed to save account posting');
    }
  };

  // Settlement CRUD: Open Add Dialog
  const handleOpenAddSettlement = (type) => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    setSettlementForm({ displayName: '', amount: '', date: new Date().toISOString().split('T')[0] });
    setSettlementDialog({ open: true, mode: 'add', type, item: null });
  };

  // Settlement CRUD: Open Edit Dialog
  const handleOpenEditSettlement = (item, type) => {
    setSettlementForm({
      displayName: item.displayName || '',
      amount: Math.abs(item.amount).toString(),
      date: item.settledAt ? item.settledAt.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setSettlementDialog({ open: true, mode: 'edit', type, item });
  };

  // Settlement CRUD: Save (Add/Edit) with P&L double-entry
  const handleSaveSettlement = async () => {
    const { mode, type, item } = settlementDialog;
    const amount = parseFloat(settlementForm.amount);
    
    if (!settlementForm.displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }
    
    // Find P&L account for double-entry
    const plAccount = clients.find(c => c.is_system_account && c.system_account_type === 'PL');
    if (!plAccount) {
      toast.error('P&L account not found. Please contact support.');
      return;
    }

    try {
      const timestamp = new Date(settlementForm.date).toISOString();
      // For To Pay: store as negative, For To Receive: store as positive
      const finalAmount = type === 'pay' ? -Math.abs(amount) : Math.abs(amount);
      const entryId = `manual_${generateId()}`;

      if (mode === 'add') {
        // Add new settlement with P&L double-entry
        const newSettlements = [];
        
        // Client entry
        const clientSettlement = {
          id: generateId(),
          entryId: entryId,
          accountType: 'MANUAL',
          clientId: selectedClient.client_id,
          clientName: selectedClient.name,
          displayName: settlementForm.displayName,
          amount: finalAmount,
          settledAt: timestamp
        };
        newSettlements.push(clientSettlement);
        
        // P&L contra entry (opposite amount)
        const plContraSettlement = {
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${settlementForm.displayName} (${selectedClient.name})`,
          amount: -finalAmount, // Opposite of client entry
          settledAt: timestamp
        };
        newSettlements.push(plContraSettlement);

        const updatedSettlements = [...settlements, ...newSettlements];
        setSettlements(updatedSettlements);

        // Save to backend
        try {
          await mondaySettlingApi.createSettlements(newSettlements);
        } catch (e) {
          console.warn('Backend save failed:', e);
        }

        toast.success('Settlement entry added');
      } else {
        // Edit existing settlement - also update the P&L contra entry
        const updatedSettlements = settlements.map(s => {
          if (s.id === item.id) {
            return {
              ...s,
              displayName: settlementForm.displayName,
              amount: finalAmount,
              settledAt: timestamp
            };
          }
          // Find and update the corresponding P&L entry
          if (s.entryId === item.entryId && s.accountType === 'PL_CONTRA') {
            return {
              ...s,
              displayName: `${settlementForm.displayName} (${selectedClient.name})`,
              amount: -finalAmount,
              settledAt: timestamp
            };
          }
          return s;
        });
        setSettlements(updatedSettlements);

        // Update backend - both main entry AND P&L contra entry
        try {
          // Update main entry
          await mondaySettlingApi.updateSettlement(item.id, {
            displayName: settlementForm.displayName,
            amount: finalAmount,
            settledAt: timestamp
          });
          
          // Also update the corresponding P&L contra entry
          const plContraEntry = settlements.find(s => s.entryId === item.entryId && s.accountType === 'PL_CONTRA');
          if (plContraEntry) {
            await mondaySettlingApi.updateSettlement(plContraEntry.id, {
              displayName: `${settlementForm.displayName} (${selectedClient.name})`,
              amount: -finalAmount, // Opposite of client entry
              settledAt: timestamp
            });
          }
        } catch (e) {
          console.warn('Backend update failed:', e);
        }

        toast.success('Settlement entry updated');
      }

      // Refresh To Pay / To Receive lists
      const clientSettlements = (mode === 'add' ? [...settlements, { id: generateId(), clientName: selectedClient.name, displayName: settlementForm.displayName, amount: finalAmount, settledAt: timestamp }] : settlements.map(s => s.id === item?.id ? { ...s, displayName: settlementForm.displayName, amount: finalAmount, settledAt: timestamp } : s)).filter(s =>
        s.clientName === selectedClient.name || s.clientId === selectedClient.client_id || s.clientId === selectedClient.name
      );
      setToPayItems(clientSettlements.filter(s => s.amount < 0));
      setToReceiveItems(clientSettlements.filter(s => s.amount > 0));

      setSettlementDialog({ open: false, mode: 'add', type: 'pay', item: null });
    } catch (error) {
      console.error('Settlement save error:', error);
      toast.error('Failed to save settlement');
    }
  };

  // Settlement CRUD: Delete with P&L double-entry cleanup
  const handleDeleteSettlement = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.displayName}"?`)) {
      return;
    }

    try {
      // Remove the item and its corresponding P&L contra entry (same entryId)
      const updatedSettlements = settlements.filter(s => 
        s.id !== item.id && !(s.entryId === item.entryId && s.accountType === 'PL_CONTRA')
      );
      setSettlements(updatedSettlements);

      // Delete from backend
      try {
        await mondaySettlingApi.deleteSettlement(item.id);
        // Also delete P&L contra entries with same entryId
        const plContraEntry = settlements.find(s => s.entryId === item.entryId && s.accountType === 'PL_CONTRA');
        if (plContraEntry) {
          await mondaySettlingApi.deleteSettlement(plContraEntry.id);
        }
      } catch (e) {
        console.warn('Backend delete failed:', e);
      }

      // Refresh To Pay / To Receive lists
      const clientSettlements = updatedSettlements.filter(s =>
        s.clientName === selectedClient.name || s.clientId === selectedClient.client_id || s.clientId === selectedClient.name
      );
      setToPayItems(clientSettlements.filter(s => s.amount < 0));
      setToReceiveItems(clientSettlements.filter(s => s.amount > 0));

      toast.success('Settlement entry deleted');
    } catch (error) {
      console.error('Settlement delete error:', error);
      toast.error('Failed to delete settlement');
    }
  };

  // Settlement: Print
  const handlePrintSettlement = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    const balance = getClientBalance(selectedClient);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Settlement - ${selectedClient.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #DAA520; padding-bottom: 10px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .balance { font-size: 18px; font-weight: bold; color: ${balance >= 0 ? '#2563eb' : '#dc2626'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          .amount { text-align: right; }
          .pay { color: #dc2626; }
          .receive { color: #2563eb; }
          .total-row { font-weight: bold; background-color: #f9f9f9; }
          .section { margin-bottom: 30px; }
          .print-date { color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>YellowGold Pro - Settlement Statement</h1>
        <div class="header">
          <div>
            <strong>Client:</strong> ${selectedClient.name}<br/>
            <strong>Current Balance:</strong> <span class="balance">${balance.toFixed(2)}</span>
          </div>
          <div class="print-date">
            Printed on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </div>
        
        <div class="section">
          <h3 style="color: #dc2626;">To Pay (${toPayItems.length})</h3>
          <table>
            <thead><tr><th>Date</th><th>Display Name</th><th class="amount">Amount</th></tr></thead>
            <tbody>
              ${toPayItems.map(item => `
                <tr>
                  <td>${format(new Date(item.settledAt), 'dd/MM/yyyy')}</td>
                  <td>${item.displayName}</td>
                  <td class="amount pay">${Math.abs(item.amount).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2" style="text-align: right;">Total:</td>
                <td class="amount pay">${toPayItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <h3 style="color: #2563eb;">To Receive (${toReceiveItems.length})</h3>
          <table>
            <thead><tr><th>Date</th><th>Display Name</th><th class="amount">Amount</th></tr></thead>
            <tbody>
              ${toReceiveItems.map(item => `
                <tr>
                  <td>${format(new Date(item.settledAt), 'dd/MM/yyyy')}</td>
                  <td>${item.displayName}</td>
                  <td class="amount receive">${Math.abs(item.amount).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2" style="text-align: right;">Total:</td>
                <td class="amount receive">${toReceiveItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Settlement: Export PDF for selected client
  const handleExportSettlementPDF = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const doc = new jsPDF();
    const balance = getClientBalance(selectedClient);
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(218, 165, 32); // Gold color
    doc.text('YellowGold Pro - Settlement Statement', 14, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Client: ${selectedClient.name}`, 14, 35);
    doc.text(`Current Balance: ${balance.toFixed(2)}`, 14, 42);
    doc.text(`Date: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 49);
    
    let yPos = 60;
    
    // To Pay section
    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38); // Red
    doc.text(`To Pay (${toPayItems.length})`, 14, yPos);
    yPos += 5;
    
    if (toPayItems.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Display Name', 'Amount']],
        body: toPayItems.map(item => [
          format(new Date(item.settledAt), 'dd/MM/yyyy'),
          item.displayName,
          Math.abs(item.amount).toFixed(2)
        ]),
        foot: [['', 'Total:', toPayItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)]],
        headStyles: { fillColor: [220, 38, 38] },
        footStyles: { fillColor: [245, 245, 245], textColor: [220, 38, 38], fontStyle: 'bold' }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    } else {
      yPos += 10;
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text('No items to pay', 14, yPos);
      yPos += 15;
    }
    
    // To Receive section
    doc.setFontSize(14);
    doc.setTextColor(37, 99, 235); // Blue
    doc.text(`To Receive (${toReceiveItems.length})`, 14, yPos);
    yPos += 5;
    
    if (toReceiveItems.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Display Name', 'Amount']],
        body: toReceiveItems.map(item => [
          format(new Date(item.settledAt), 'dd/MM/yyyy'),
          item.displayName,
          Math.abs(item.amount).toFixed(2)
        ]),
        foot: [['', 'Total:', toReceiveItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)]],
        headStyles: { fillColor: [37, 99, 235] },
        footStyles: { fillColor: [245, 245, 245], textColor: [37, 99, 235], fontStyle: 'bold' }
      });
    }
    
    doc.save(`settlement_${selectedClient.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`);
    toast.success('Settlement PDF exported');
  };

  // Toggle tallied status for a settlement item
  const handleToggleTallied = async (item) => {
    try {
      const newTallied = !item.tallied;
      const response = await fetch(`${API_URL}/api/settlements/${item.id}/tallied`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ settlement_id: item.id, tallied: newTallied })
      });
      
      if (response.ok) {
        // Update local state
        if (item.amount < 0) {
          setToPayItems(prev => prev.map(i => 
            i.id === item.id ? { ...i, tallied: newTallied } : i
          ));
        } else {
          setToReceiveItems(prev => prev.map(i => 
            i.id === item.id ? { ...i, tallied: newTallied } : i
          ));
        }
        toast.success(`Marked as ${newTallied ? 'tallied' : 'not tallied'}`);
      } else {
        toast.error('Failed to update tallied status');
      }
    } catch (error) {
      console.error('Error toggling tallied:', error);
      toast.error('Error updating tallied status');
    }
  };

  // Get display name based on source type
  // For Account Posting: show targetClientName (the other party)
  // For special sources: show predefined names
  const getDisplayName = (item) => {
    const sourceType = item.sourceType || item.source_type;
    if (sourceType === 'MONDAY') return 'Online Settlement';
    if (sourceType === 'MCXFO') return 'MCX & FO';
    
    // For Account Posting entries, prefer targetClientName
    if (item.targetClientName) {
      return item.targetClientName;
    }
    
    // Fallback to displayName or other fields
    return item.displayName || item.display_name || item.client_name || '-';
  };

  // Get reference/narration based on source type
  // For Account Posting: show narration text
  // For special sources: show displayIdName
  const getReference = (item) => {
    const sourceType = item.sourceType || item.source_type;
    if (sourceType === 'MONDAY' || sourceType === 'MCXFO') {
      return item.displayIdName || item.display_id_name || item.narration || '';
    }
    
    // For Account Posting, show the narration field
    return item.narration || item.reference || item.notes || '';
  };

  // Settlement: Exit (clear selected client)
  const handleExitSettlement = () => {
    setSelectedClient(null);
    setToPayItems([]);
    setToReceiveItems([]);
  };

  // Handle Ledger button click
  const handleOpenLedger = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    setLedgerDialog({
      open: true,
      clientId: selectedClient.client_id,
      clientName: selectedClient.name,
      isHistorical: false,
      mondayFinalId: null
    });
  };

  // Handle Monday Final button click
  const handleOpenMondayFinal = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    const balance = getClientBalance(selectedClient);
    // Count all transactions that will be locked: match/session settlements + To Pay + To Receive
    const totalTransactions = clientSettlements.length + toPayItems.length + toReceiveItems.length;
    setMondayFinalDialog({
      open: true,
      clientId: selectedClient.client_id,
      clientName: selectedClient.name,
      transactionCount: totalTransactions,
      currentBalance: balance
    });
  };

  // Handle Monday Final success - refresh data
  const handleMondayFinalSuccess = async (result) => {
    // Refresh all data (clients, settlements, monday_settlements)
    await fetchData();
    
    // Re-fetch monday-settling data specifically and wait for it
    let newSettlements = [];
    try {
      const [entriesRes, settlementsRes] = await Promise.all([
        fetch(`${API_URL}/api/monday-settling/entries?_t=${Date.now()}`, { credentials: 'include', cache: 'no-store' }),
        fetch(`${API_URL}/api/monday-settling/settlements?_t=${Date.now()}`, { credentials: 'include', cache: 'no-store' })
      ]);
      
      if (entriesRes.ok) {
        const entriesData = await entriesRes.json();
        setEntries(entriesData);
      }
      if (settlementsRes.ok) {
        newSettlements = await settlementsRes.json();
        setSettlements(newSettlements);
      }
    } catch (error) {
      console.error('Error refreshing settlements:', error);
    }
    
    // Re-select client to refresh its view with updated old_balance
    if (selectedClient) {
      // Fetch fresh client data
      try {
        const clientsRes = await fetch(`${API_URL}/api/clients`, { credentials: 'include' });
        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData);
          const updatedClient = clientsData.find(c => c.client_id === selectedClient.client_id);
          if (updatedClient) {
            // Use the NEW settlements data directly instead of relying on state
            const clientSettlementsFromMonday = newSettlements.filter(s => 
              (s.clientName === updatedClient.name || s.clientId === updatedClient.client_id || s.clientId === updatedClient.name) &&
              !s.is_locked && s.is_visible !== false
            );
            
            // Update To Pay / To Receive with fresh data
            setToPayItems(clientSettlementsFromMonday.filter(s => s.amount < 0));
            setToReceiveItems(clientSettlementsFromMonday.filter(s => s.amount > 0));
            
            // Also update the selected client reference
            setSelectedClient(updatedClient);
            
            // Fetch client settlements (match/session)
            try {
              const allClientSettlements = await settlementsApi.list(updatedClient.client_id);
              const filteredSettlements = allClientSettlements.filter(s => 
                (s.source_type === 'MATCH' || s.source_type === 'SESSION' || s.match_id || s.session_id) &&
                !s.is_locked
              );
              setClientSettlements(filteredSettlements);
            } catch (err) {
              console.error('Error fetching client settlements:', err);
              setClientSettlements([]);
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing clients:', error);
      }
    }
  };

  // Handle Historical Records button click
  const handleOpenHistorical = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    setHistoricalDialog({
      open: true,
      clientId: selectedClient.client_id,
      clientName: selectedClient.name
    });
  };

  // Handle viewing historical ledger from HistoricalRecordsModal
  // Now accepts array of mondayFinalIds for multiple selection
  const handleViewHistoricalLedger = (mondayFinalIds) => {
    // Convert to array if single ID passed
    const idsArray = Array.isArray(mondayFinalIds) ? mondayFinalIds : [mondayFinalIds];
    
    setLedgerDialog({
      open: true,
      clientId: selectedClient?.client_id,
      clientName: selectedClient?.name || '',
      isHistorical: true,
      mondayFinalIds: idsArray
    });
  };

  // Editable cell component for spreadsheet-style input
  const EditableCell = ({ value, onChange, onKeyDown, type = 'text', step, placeholder, rowIdx, colIdx, disabled }) => {
    return (
      <input
        ref={(ref) => setCellRef(rowIdx, colIdx, ref)}
        type={type}
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
        onKeyDown={(e) => onKeyDown(e, rowIdx, colIdx)}
        onFocus={(e) => {
          e.target.select();
          setFocusedCell({ row: rowIdx, col: colIdx });
        }}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full h-6 px-1 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
        style={{ minWidth: type === 'number' ? '60px' : '80px' }}
      />
    );
  };

  return (
    <div className="h-full flex flex-col" data-testid="settlement-page">
      {/* Settlement Content - No Tabs */}
      <div className="flex-1 flex flex-col">
        <div className="grid grid-cols-12 gap-2 items-start">
          {/* All Clients Grid (Left) */}
          <div className="col-span-3 flex flex-col border rounded h-[calc(100vh-180px)]">
            <div className="bg-muted px-2 py-1 text-xs font-medium border-b shrink-0">All Clients</div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-1 py-1 text-left">Client Name</th>
                    <th className="px-1 py-1 text-right">Current Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(client => {
                    const balance = getClientBalance(client);
                    return (
                      <tr 
                        key={client.client_id}
                        className={`border-b cursor-pointer hover:bg-amber-500/20 ${
                          selectedClient?.client_id === client.client_id ? 'bg-amber-500/30 font-semibold' : ''
                        }`}
                        onClick={() => handleSelectClient(client)}
                        style={{ height: '24px' }}
                      >
                        <td className="px-1 py-0.5 truncate max-w-[100px]" title={client.name}>
                          {client.name}
                        </td>
                        <td className={`px-1 py-0.5 text-right ${balance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {balance.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Section */}
          <div className="col-span-9 flex flex-col gap-2">
              {/* Top Header Grid - Old Balance | Client Name | Current Balance */}
              {selectedClient && (
                <div className="border rounded bg-muted/20">
                  <div className="grid grid-cols-3 text-center">
                    <div className="px-3 py-2 border-r">
                      <div className="text-xs text-muted-foreground">Old Balance</div>
                      <div className={`font-mono font-bold text-lg ${
                        (selectedClient.old_balance || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'
                      }`}>
                        {(selectedClient.old_balance || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="px-3 py-2 border-r bg-amber-500/10">
                      <div className="text-xs text-muted-foreground">Client</div>
                      <div className="font-bold text-lg text-amber-500">{selectedClient.name}</div>
                    </div>
                    <div className="px-3 py-2">
                      <div className="text-xs text-muted-foreground">Current Balance</div>
                      <div className={`font-mono font-bold text-lg ${
                        getClientBalance(selectedClient) >= 0 ? 'text-blue-500' : 'text-red-500'
                      }`}>
                        {getClientBalance(selectedClient).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Second Row: Transaction Table (Left) + Action Buttons (Right) */}
              {selectedClient && (
                <div className="grid grid-cols-3 gap-2">
                  {/* Left: Settlement History Table with Commission Details */}
                  <div className="col-span-2 border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium border-r w-16">Type</th>
                          <th className="px-2 py-1.5 text-left font-medium border-r">Comment</th>
                          <th className="px-2 py-1.5 text-right font-medium border-r w-20">Gross</th>
                          <th className="px-2 py-1.5 text-right font-medium border-r w-16">Comm</th>
                          <th className="px-2 py-1.5 text-right font-medium border-r w-16">Ref Comm</th>
                          <th className="px-2 py-1.5 text-right font-medium border-r w-20">Net</th>
                          <th className="px-2 py-1.5 text-left font-medium w-20">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Show all settlement entries */}
                        {clientSettlements.map(item => {
                          const entryType = item.entry_type || 'CLIENT';
                          const isRefCommission = entryType === 'REF_COMMISSION';
                          const isPLBook = entryType === 'PL_BOOK';
                          
                          return (
                            <tr key={item.settlement_id} className={`border-b hover:bg-muted/50 ${isPLBook ? 'bg-purple-50 dark:bg-purple-950/30' : isRefCommission ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}>
                              <td className="px-2 py-1 border-r text-xs">
                                {isPLBook ? (
                                  <span className="text-purple-600 font-medium">P&L Book</span>
                                ) : isRefCommission ? (
                                  <span className="text-amber-600 font-medium">Ref Comm</span>
                                ) : (
                                  <span className="text-gray-500">Client</span>
                                )}
                              </td>
                              <td className="px-2 py-1 border-r">
                                {(item.match_id || item.session_id) ? (
                                  <button 
                                    className="text-primary hover:underline cursor-pointer text-left"
                                    onClick={() => {
                                      if (isPLBook) {
                                        handlePLBookClick(item);
                                      } else if (isRefCommission && item.source_clients) {
                                        handleRefCommClick(item);
                                      } else {
                                        handleMatchClick(item);
                                      }
                                    }}
                                  >
                                    {item.comment || item.match_name || 'Settlement'}
                                  </button>
                                ) : (
                                  <span>{item.comment || item.match_name || 'Settlement'}</span>
                                )}
                              </td>
                              <td className={`px-2 py-1 text-right border-r font-mono ${(item.gross_amount || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                {(item.gross_amount || 0).toFixed(2)}
                              </td>
                              <td className={`px-2 py-1 text-right border-r font-mono ${(item.commission || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(item.commission || 0).toFixed(2)}
                              </td>
                              <td className={`px-2 py-1 text-right border-r font-mono ${(item.ref_commission || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(item.ref_commission || 0).toFixed(2)}
                              </td>
                              <td className={`px-2 py-1 text-right border-r font-mono font-semibold ${(item.net_amount || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                {(item.net_amount || 0).toFixed(2)}
                              </td>
                              <td className="px-2 py-1">
                                {item.settled_at ? format(new Date(item.settled_at), 'dd/MM/yy') : '-'}
                              </td>
                            </tr>
                          );
                        })}
                        {clientSettlements.length === 0 && (
                          <tr>
                            <td colSpan={7} className="px-2 py-3 text-center text-muted-foreground">
                              No settlements found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-muted/50">
                        <tr className="font-semibold">
                          <td className="px-2 py-1.5 border-r"></td>
                          <td className="px-2 py-1.5 border-r text-right">Total:</td>
                          <td className={`px-2 py-1.5 text-right border-r font-mono ${
                            clientSettlements.reduce((sum, s) => sum + (s.gross_amount || 0), 0) >= 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            {clientSettlements.reduce((sum, s) => sum + (s.gross_amount || 0), 0).toFixed(2)}
                          </td>
                          <td className={`px-2 py-1.5 text-right border-r font-mono ${
                            clientSettlements.reduce((sum, s) => sum + (s.commission || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {clientSettlements.reduce((sum, s) => sum + (s.commission || 0), 0).toFixed(2)}
                          </td>
                          <td className={`px-2 py-1.5 text-right border-r font-mono ${
                            clientSettlements.reduce((sum, s) => sum + (s.ref_commission || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {clientSettlements.reduce((sum, s) => sum + (s.ref_commission || 0), 0).toFixed(2)}
                          </td>
                          <td className={`px-2 py-1.5 text-right border-r font-mono ${
                            clientSettlements.reduce((sum, s) => sum + (s.net_amount || 0), 0) >= 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            {clientSettlements.reduce((sum, s) => sum + (s.net_amount || 0), 0).toFixed(2)}
                          </td>
                          <td className="px-2 py-1.5"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Right: Action Buttons */}
                  <div className="col-span-1 border rounded p-2 flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full h-8 text-xs justify-start gap-2"
                      onClick={handleOpenMondayFinal}
                      data-testid="monday-final-btn"
                    >
                      <Lock className="h-3.5 w-3.5" />
                      Monday Final
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full h-8 text-xs justify-start gap-2"
                      onClick={handleOpenLedger}
                      data-testid="ledger-btn"
                    >
                      <BookOpen className="h-3.5 w-3.5" />
                      Ledger
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full h-8 text-xs justify-start gap-2"
                      onClick={handleOpenHistorical}
                      data-testid="historical-records-btn"
                    >
                      <History className="h-3.5 w-3.5" />
                      Last week records
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full h-8 text-xs justify-start gap-2" 
                      onClick={handlePrintSettlement}
                      data-testid="print-settlement-btn"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      Print
                    </Button>
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                      <Checkbox id="hideZeroBalance" />
                      <label htmlFor="hideZeroBalance" className="text-xs">Hide 0.00 Balance A/c</label>
                    </div>
                  </div>
                </div>
              )}

              {/* No client selected message */}
              {!selectedClient && (
                <div className="bg-muted/30 rounded p-4 border text-center text-muted-foreground">
                  Select a client from the list to view settlement details
                </div>
              )}

              {/* To Pay / To Receive Grids */}
              <div className="grid grid-cols-2 gap-2">
                {/* To Pay */}
                <div className="flex flex-col border rounded h-[200px]">
                  <div className="bg-red-500/20 px-2 py-1 text-xs font-medium border-b text-red-400 shrink-0">
                    To Pay ({toPayItems.length})
                  </div>
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-1 py-1 w-8 text-center">✓</th>
                          <th className="px-1 py-1 text-left">Date</th>
                          <th className="px-1 py-1 text-left">Client Name</th>
                          <th className="px-1 py-1 text-left">Reference</th>
                          <th className="px-1 py-1 text-right">Amount</th>
                          <th className="px-1 py-1 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {toPayItems.map(item => (
                          <tr 
                            key={item.id} 
                            className={cn(
                              "border-b hover:bg-red-500/20",
                              item.tallied && "bg-emerald-500/10"
                            )} 
                            style={{ height: '24px' }}
                          >
                            <td className="px-1 py-0.5 text-center">
                              <Checkbox 
                                checked={item.tallied || false}
                                onCheckedChange={() => handleToggleTallied(item)}
                                className="h-3.5 w-3.5"
                                data-testid={`tallied-checkbox-pay-${item.id}`}
                              />
                            </td>
                            <td className="px-1 py-0.5">{item.settledAt ? format(new Date(item.settledAt), 'dd/MM') : '-'}</td>
                            <td className="px-1 py-0.5">{getDisplayName(item)}</td>
                            <td className="px-1 py-0.5 text-muted-foreground truncate max-w-[100px]" title={getReference(item)}>
                              {getReference(item)}
                            </td>
                            <td className="px-1 py-0.5 text-right text-red-500">
                              {Math.abs(item.amount).toFixed(2)}
                            </td>
                            <td className="px-1 py-0.5">
                              <div className="flex gap-0.5">
                                <button 
                                  className="text-primary hover:text-primary/80 text-xs" 
                                  onClick={() => {
                                    setSettlementForm({ 
                                      displayName: item.displayName, 
                                      amount: Math.abs(item.amount).toString(),
                                      date: item.settledAt ? item.settledAt.split('T')[0] : new Date().toISOString().split('T')[0]
                                    });
                                    setSettlementDialog({ open: true, mode: 'edit', type: 'pay', item });
                                  }}
                                  title="Edit"
                                >✎</button>
                                <button 
                                  className="text-red-500 hover:text-red-400 text-xs" 
                                  onClick={() => handleDeleteSettlement(item)}
                                  title="Delete"
                                >✕</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {toPayItems.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">
                              No items to pay
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-muted/50 font-medium sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="px-1 py-1 text-right">Total:</td>
                          <td className="px-1 py-1 text-right text-red-600">
                            {toPayItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {/* CRUD Controls */}
                  <div className="flex gap-1 p-1 border-t bg-muted/30 shrink-0">
                    <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => handleOpenAddSettlement('pay')}>Add</Button>
                  </div>
                </div>

                {/* To Receive */}
                <div className="flex flex-col border rounded h-[200px]">
                  <div className="bg-emerald-500/20 px-2 py-1 text-xs font-medium border-b text-emerald-400 shrink-0">
                    To Receive ({toReceiveItems.length})
                  </div>
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-1 py-1 w-8 text-center">✓</th>
                          <th className="px-1 py-1 text-left">Date</th>
                          <th className="px-1 py-1 text-left">Client Name</th>
                          <th className="px-1 py-1 text-left">Reference</th>
                          <th className="px-1 py-1 text-right">Amount</th>
                          <th className="px-1 py-1 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {toReceiveItems.map(item => (
                          <tr 
                            key={item.id} 
                            className={cn(
                              "border-b hover:bg-emerald-500/20",
                              item.tallied && "bg-emerald-500/10"
                            )} 
                            style={{ height: '24px' }}
                          >
                            <td className="px-1 py-0.5 text-center">
                              <Checkbox 
                                checked={item.tallied || false}
                                onCheckedChange={() => handleToggleTallied(item)}
                                className="h-3.5 w-3.5"
                                data-testid={`tallied-checkbox-receive-${item.id}`}
                              />
                            </td>
                            <td className="px-1 py-0.5">{item.settledAt ? format(new Date(item.settledAt), 'dd/MM') : '-'}</td>
                            <td className="px-1 py-0.5">{getDisplayName(item)}</td>
                            <td className="px-1 py-0.5 text-muted-foreground truncate max-w-[100px]" title={getReference(item)}>
                              {getReference(item)}
                            </td>
                            <td className="px-1 py-0.5 text-right text-emerald-500">
                              {Math.abs(item.amount).toFixed(2)}
                            </td>
                            <td className="px-1 py-0.5">
                              <div className="flex gap-0.5">
                                <button 
                                  className="text-primary hover:text-primary/80 text-xs" 
                                  onClick={() => {
                                    setSettlementForm({ 
                                      displayName: item.displayName, 
                                      amount: Math.abs(item.amount).toString(),
                                      date: item.settledAt ? item.settledAt.split('T')[0] : new Date().toISOString().split('T')[0]
                                    });
                                    setSettlementDialog({ open: true, mode: 'edit', type: 'receive', item });
                                  }}
                                  title="Edit"
                                >✎</button>
                                <button 
                                  className="text-red-500 hover:text-red-400 text-xs" 
                                  onClick={() => handleDeleteSettlement(item)}
                                  title="Delete"
                                >✕</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {toReceiveItems.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">
                              No items to receive
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-muted/50 font-medium sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="px-1 py-1 text-right">Total:</td>
                          <td className="px-1 py-1 text-right text-emerald-600">
                            {toReceiveItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {/* CRUD Controls */}
                  <div className="flex gap-1 p-1 border-t bg-muted/30 shrink-0">
                    <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => handleOpenAddSettlement('receive')}>Add</Button>
                  </div>
                </div>
              </div>

              {/* Account Posting Section (Bottom) */}
              <div className="border rounded p-2 bg-muted/20">
                <div className="text-xs font-medium mb-2 text-muted-foreground">Account Posting</div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Balance</Label>
                    <Input 
                      type="text" 
                      className="h-7 text-xs" 
                      placeholder="0.00"
                      value={selectedClient ? getClientBalance(selectedClient).toFixed(2) : '0.00'}
                      readOnly
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Client</Label>
                    <Select value={postingClient} onValueChange={setPostingClient}>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.filter(c => c.name !== selectedClient?.name).map(c => (
                          <SelectItem key={c.client_id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Amount <span className="text-muted-foreground">(−ve=Pay, +ve=Receive)</span></Label>
                    <Input 
                      type="number" 
                      className="h-7 text-xs" 
                      placeholder="-ve to Pay, +ve to Receive"
                      value={postingAmount}
                      onChange={(e) => setPostingAmount(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Narration</Label>
                    <Input 
                      type="text" 
                      className="h-7 text-xs" 
                      placeholder="Enter narration"
                      value={postingNarration}
                      onChange={(e) => setPostingNarration(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-2 justify-end">
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={handleAccountPosting}
                    disabled={!selectedClient || !postingClient || !postingAmount}
                  >
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExitSettlement}>
                    Exit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Settlement Add/Edit Dialog */}
      <Dialog open={settlementDialog.open} onOpenChange={(open) => setSettlementDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {settlementDialog.mode === 'add' ? 'Add' : 'Edit'} {settlementDialog.type === 'pay' ? 'To Pay' : 'To Receive'} Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Display Name</Label>
              <Input
                value={settlementForm.displayName}
                onChange={(e) => setSettlementForm(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="Enter display name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Amount</Label>
              <Input
                type="number"
                value={settlementForm.amount}
                onChange={(e) => setSettlementForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="mt-1"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label className="text-sm">Date</Label>
              <Input
                type="date"
                value={settlementForm.date}
                onChange={(e) => setSettlementForm(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettlementDialog({ open: false, mode: 'add', type: 'pay', item: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettlement}>
              {settlementDialog.mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle All Confirmation Dialog */}
      <Dialog open={settleAllConfirm} onOpenChange={setSettleAllConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle All Accounts</DialogTitle>
            <DialogDescription>
              This will settle all {entries.filter(e => e.status === STATUS.NEW).length} unsettled entries. 
              Amounts will be posted to respective client and parent accounts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleAllConfirm(false)}>Cancel</Button>
            <Button onClick={handleSettleAll} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="h-4 w-4 mr-1" />
              Confirm Settle All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Match/Session Transactions Dialog - Using TrialBalanceDetailDialog */}
      <TrialBalanceDetailDialog
        open={matchTxnDialog.open}
        onClose={() => setMatchTxnDialog({ 
          open: false, 
          clientId: null, 
          clientName: '', 
          matchIds: [], 
          sessionIds: [],
          trialBalanceItem: null
        })}
        clientId={matchTxnDialog.clientId}
        clientName={matchTxnDialog.clientName}
        matchIds={matchTxnDialog.matchIds || []}
        sessionIds={matchTxnDialog.sessionIds || []}
        trialBalanceItem={matchTxnDialog.trialBalanceItem}
      />

      {/* Reference Commission Source Details Dialog */}
      <Dialog open={refCommDialog.open} onOpenChange={(open) => setRefCommDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-amber-600">Reference Commission Details</span>
            </DialogTitle>
            <DialogDescription>
              {refCommDialog.settlement?.comment || 'Commission breakdown by client'}
            </DialogDescription>
          </DialogHeader>
          
          {refCommDialog.settlement?.source_clients && (
            <div className="space-y-3">
              <table className="w-full text-sm border rounded">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-right">Book</th>
                    <th className="px-3 py-2 text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {refCommDialog.settlement.source_clients.map((src, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{src.client_name}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-600">
                        {src.book?.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-amber-600">
                        {src.commission?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/50">
                  <tr className="font-semibold">
                    <td className="px-3 py-2">Total Ref Commission</td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right font-mono text-amber-600">
                      {refCommDialog.settlement.source_clients.reduce((sum, s) => sum + (s.commission || 0), 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              
              <div className="p-3 bg-amber-50 rounded border border-amber-200 text-sm">
                <strong>Net Amount to Pay:</strong>{' '}
                <span className="font-mono text-red-600">
                  {Math.abs(refCommDialog.settlement.net_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefCommDialog({ open: false, settlement: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger Report Dialog */}
      <LedgerReportDialog
        open={ledgerDialog.open}
        onClose={() => setLedgerDialog({ open: false, clientId: null, clientName: '', isHistorical: false, mondayFinalIds: [] })}
        clientId={ledgerDialog.clientId}
        clientName={ledgerDialog.clientName}
        isHistorical={ledgerDialog.isHistorical}
        mondayFinalIds={ledgerDialog.mondayFinalIds}
      />

      {/* Monday Final Modal */}
      <MondayFinalModal
        open={mondayFinalDialog.open}
        onClose={() => setMondayFinalDialog({ open: false, clientId: null, clientName: '', transactionCount: 0, currentBalance: 0 })}
        clientId={mondayFinalDialog.clientId}
        clientName={mondayFinalDialog.clientName}
        transactionCount={mondayFinalDialog.transactionCount}
        currentBalance={mondayFinalDialog.currentBalance}
        onSuccess={handleMondayFinalSuccess}
      />

      {/* Historical Records Modal */}
      <HistoricalRecordsModal
        open={historicalDialog.open}
        onClose={() => setHistoricalDialog({ open: false, clientId: null, clientName: '' })}
        clientId={historicalDialog.clientId}
        clientName={historicalDialog.clientName}
        onViewLedger={handleViewHistoricalLedger}
      />
    </div>
  );
}
